// OverflowTabRow — the inline second row of the contract tab bar that holds the
// tabs which did not fit in the primary row. Rendered only while open. Uses the
// secondary TabButton variant so the row reads as a lower-priority continuation
// of the primary row, with a trailing icon-only close button that hides the row
// and returns focus to the More button.

import { X } from "lucide-react";
import { TabButton } from "../../common";
import { getTabDef, type TabSlug } from "./tabRegistry";
import TabLabelWithAttention from "./TabLabelWithAttention";

interface OverflowTabRowProps {
  slugs: TabSlug[];
  activeTab: TabSlug;
  badgeCounts: Partial<Record<TabSlug, number | undefined>>;
  attention: Partial<Record<TabSlug, boolean>>;
  onTabClick: (slug: TabSlug) => void;
  onClose: () => void;
  registerTab: (slug: TabSlug) => (el: HTMLButtonElement | null) => void;
}

export default function OverflowTabRow({
  slugs,
  activeTab,
  badgeCounts,
  attention,
  onTabClick,
  onClose,
  registerTab,
}: OverflowTabRowProps) {
  return (
    // The row WRAPS (no horizontal scroll anywhere is a locked requirement): at
    // narrow widths the hidden tabs flow onto multiple lines so every tab stays
    // reachable. The close button is anchored top-right of the container (not
    // `ml-auto` in the flow, which would land mid-row once tabs wrap), and the
    // tab flow reserves right padding so a tab never sits under the × button.
    <div
      id="contract-tabs-overflow-row"
      role="presentation"
      className="relative border-t border-slate-200 dark:border-slate-700"
    >
      <div className="flex flex-wrap items-center gap-x-8 gap-y-1 pr-9">
        {slugs.map((slug) => {
          const { label, icon: Icon } = getTabDef(slug);
          return (
            <TabButton
              key={slug}
              ref={registerTab(slug)}
              variant="secondary"
              active={activeTab === slug}
              panelId={`${slug}-panel`}
              icon={<Icon className="h-4 w-4" />}
              badge={badgeCounts[slug]}
              onClick={() => onTabClick(slug)}
            >
              <TabLabelWithAttention label={label} attention={attention[slug]} />
            </TabButton>
          );
        })}
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Hide extra sections"
        className="absolute right-0 top-1.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
